/** Shadcn-style date range picker — Popover trigger + Calendar in range mode,
 * with quick preset shortcuts (7d/30d/90d) alongside free-form custom selection.
 */

import * as React from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import type { DateRange, Matcher } from "react-day-picker";

import { Button } from "@/shared/ui/button";
import { Calendar } from "@/shared/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import { cn } from "@/shared/lib/utils";

/** A single quick-select shortcut, e.g. "Last 7 days" -> 7 trailing days. */
export interface DateRangePreset {
  label: string;
  days: number;
}

const DEFAULT_PRESETS: DateRangePreset[] = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
];

export interface DateRangePickerProps {
  /** Currently selected range. `undefined` renders the placeholder. */
  value?: DateRange;
  /** Called with the new range on preset click or calendar selection. */
  onChange: (range: DateRange | undefined) => void;
  /** Quick-select shortcuts shown in the popover sidebar. */
  presets?: DateRangePreset[];
  className?: string;
  align?: "start" | "center" | "end";
  placeholder?: string;
  /** Disable dates after this. Also limits month navigation to this month. */
  maxDate?: Date;
}

/** Formats a range as "Jan 20, 2026 - Feb 09, 2026", or the placeholder if empty. */
export function formatDateRangeLabel(range: DateRange | undefined, placeholder = "Select date range"): string {
  if (!range?.from) return placeholder;
  if (!range.to) return format(range.from, "LLL dd, y");
  return `${format(range.from, "LLL dd, y")} - ${format(range.to, "LLL dd, y")}`;
}

/** Builds a trailing `days`-day range ending today (inclusive of today). */
export function presetToDateRange(days: number): DateRange {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - (days - 1));
  return { from, to };
}

export function DateRangePicker({
  value,
  onChange,
  presets = DEFAULT_PRESETS,
  className,
  align = "start",
  placeholder = "Select date range",
  maxDate,
}: DateRangePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [intermediate, setIntermediate] = React.useState<DateRange | undefined>(undefined);
  const [cleared, setCleared] = React.useState(false);

  const disabled: Matcher[] = [];
  if (maxDate) {
    disabled.push({ after: maxDate });
  }

  const defaultMonth = value?.to ?? maxDate ?? value?.from;
  const effectiveValue = cleared ? undefined : (open ? intermediate : value);

  function handleSelect(range: DateRange | undefined) {
    setCleared(false);
    setIntermediate(range);
    if (range?.from && range?.to) {
      onChange(range);
    }
  }

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen);
    if (!nextOpen) setIntermediate(undefined);
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          id="date"
          variant="outline"
          aria-label="Select date range"
          data-empty={!effectiveValue}
          className={cn("w-[300px] justify-start text-left font-normal data-[empty=true]:text-muted-foreground", className)}
        >
          <CalendarIcon className="size-4 shrink-0" aria-hidden="true" />
          {effectiveValue?.from ? (
            effectiveValue.to ? (
              <>
                {format(effectiveValue.from, "LLL dd, y")} -{" "}
                {format(effectiveValue.to, "LLL dd, y")}
              </>
            ) : (
              format(effectiveValue.from, "LLL dd, y")
            )
          ) : (
            <span>{placeholder}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-auto p-0">
        <Calendar
          mode="range"
          selected={effectiveValue}
          onSelect={handleSelect}
          numberOfMonths={2}
          defaultMonth={defaultMonth}
          disabled={disabled.length ? disabled : undefined}
          endMonth={maxDate}
        />
        <div className="flex items-center justify-between gap-1 border-t border-border px-3 py-2">
          <div className="flex items-center gap-1">
            {presets.map((preset) => (
              <Button
                key={preset.label}
                type="button"
                variant="ghost"
                size="sm"
                className="justify-start font-normal"
                onClick={() => {
                  setCleared(false);
                  setIntermediate(undefined);
                  onChange(presetToDateRange(preset.days));
                  setOpen(false);
                }}
              >
                {preset.label}
              </Button>
            ))}
          </div>
          {(effectiveValue || intermediate) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="justify-end font-normal text-muted-foreground"
              onClick={() => {
                setCleared(true);
                setIntermediate(undefined);
                onChange(undefined);
              }}
            >
              Clear
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
